import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/ga";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

// Aquila Air & Heating serves Brevard County, FL only.
const BREVARD_ZIPS = new Set([
  "32754", "32759", "32775", "32780", "32781", "32782", "32783", "32796", // Mims, Scottsmoor, Titusville
  "32815", "32816", "32899", "32920", "32925", // KSC, Cape Canaveral
  "32922", "32923", "32924", "32926", "32927", // Cocoa
  "32931", "32932", // Cocoa Beach
  "32934", "32935", "32936", "32937", // Melbourne, Satellite Beach
  "32940", "32941", // Viera / Melbourne
  "32950", "32951", // Malabar, Melbourne Beach
  "32952", "32953", "32954", // Merritt Island
  "32955", "32956", // Rockledge, Sharpes
  "32949", "32976", // Grant-Valkaria
  "32901", "32902", "32903", "32904", "32905", "32906", "32907", "32908", "32909", "32910", "32911", "32912", // Palm Bay / Melbourne
]);

export default function LeadForm({ defaultService = "", compact = false }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", city: "", state: "FL", zip: "", service: defaultService,
    message: "", preferred_contact: "", preferred_time: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const zipDigits = (form.zip || "").replace(/\D/g, "").slice(0, 5);
  const outsideBrevard = zipDigits.length === 5 && !BREVARD_ZIPS.has(zipDigits);

  const { data: services = [] } = useQuery({
    queryKey: ["services-lite"],
    queryFn: async () => (await api.get("/services")).data,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));


  const submit = async (e) => {
    e.preventDefault();
    const required = { name: "Name", phone: "Phone", email: "Email", address: "Street Address", city: "City", state: "State", zip: "ZIP Code", service: "Service Needed", preferred_contact: "Preferred Contact" };
    if (!compact) required.message = "How can we help";
    const missing = Object.entries(required).filter(([k]) => !String(form[k] || "").trim()).map(([, label]) => label);
    if (missing.length) {
      toast.error(`Please complete all fields: ${missing.join(", ")}.`);
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source_page: window.location.pathname });
      trackEvent("form_submit", { form: "lead_form", service: form.service });
      trackEvent("generate_lead", { service: form.service });
      setDone(true);
      toast.success("Request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="lead-form-success">
        <CheckCircle2 className="h-14 w-14 text-cool mx-auto" />
        <h3 className="mt-4 text-2xl font-bold text-navy">Request Received!</h3>
        <p className="mt-2 text-slate-600">A member of our Space Coast team will reach out shortly. For urgent service, please call us.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
      <div className="rounded-md bg-cool/10 border border-cool/30 px-3 py-2 text-sm text-navy flex items-center gap-2" data-testid="lead-brevard-note">
        <MapPin className="h-4 w-4 text-cool shrink-0" />
        <span>Proudly serving <strong>all of Brevard County</strong> — Mims to Palm Bay, Titusville, Cocoa, Rockledge, Melbourne, Merritt Island &amp; the beaches.</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="lf-name" className="text-navy font-medium">Name *</Label>
          <Input id="lf-name" data-testid="lead-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" placeholder="Your name" />
        </div>
        <div>
          <Label htmlFor="lf-phone" className="text-navy font-medium">Phone *</Label>
          <Input id="lf-phone" data-testid="lead-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" placeholder="(321) 292-1189" />
        </div>
      </div>
      <div>
        <Label htmlFor="lf-address" className="text-navy font-medium">Street Address *</Label>
        <Input id="lf-address" data-testid="lead-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" placeholder="123 Main St" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="lf-city" className="text-navy font-medium">City *</Label>
          <Input id="lf-city" data-testid="lead-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" placeholder="Cocoa" />
        </div>
        <div>
          <Label htmlFor="lf-state" className="text-navy font-medium">State *</Label>
          <Input id="lf-state" data-testid="lead-state" value={form.state} onChange={(e) => set("state", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" placeholder="FL" />
        </div>
        <div>
          <Label htmlFor="lf-zip" className="text-navy font-medium">ZIP Code *</Label>
          <Input id="lf-zip" data-testid="lead-zip" value={form.zip} onChange={(e) => set("zip", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" placeholder="32922" />
          {outsideBrevard && (
            <p className="mt-1 text-xs text-amber-600 flex items-start gap-1" data-testid="lead-zip-warning">
              <span>That ZIP looks outside Brevard County — we primarily serve Brevard. Send your request anyway and we'll confirm if we can help.</span>
            </p>
          )}
        </div>
      </div>
      <div>
        <div>
          <Label htmlFor="lf-email" className="text-navy font-medium">Email *</Label>
          <Input id="lf-email" data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" placeholder="you@email.com" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-navy font-medium">Service Needed *</Label>
          <Select value={form.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger aria-label="Select a service" className="mt-1 border-slate-300 focus:ring-cool" data-testid="lead-service">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {services.map((s) => (
                <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>
              ))}
              <SelectItem value="Other / Not Sure">Other / Not Sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-navy font-medium">Preferred Contact *</Label>
          <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
            <SelectTrigger aria-label="Preferred contact method" className="mt-1 border-slate-300 focus:ring-cool" data-testid="lead-contact-method">
              <SelectValue placeholder="Phone or Email" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Phone">Phone Call</SelectItem>
              <SelectItem value="Text">Text Message</SelectItem>
              <SelectItem value="Email">Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {!compact && (
        <div>
          <Label htmlFor="lf-msg" className="text-navy font-medium">How can we help? *</Label>
          <Textarea id="lf-msg" data-testid="lead-message" value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1 border-slate-300 focus-visible:ring-cool" rows={3} placeholder="Describe the problem or what you need..." />
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        data-testid="lead-submit"
        className="w-full inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-md transition-colors duration-200 disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        Request My Free Estimate
      </button>
      <p className="text-xs text-slate-500 text-center">No obligation. We respect your privacy and never share your info.</p>
    </form>
  );
}
